import React, { useMemo } from 'react';

import { ColumnElement, GridColumnProps } from '../columns/types';

import { ColumnCalculations, GridWidths, GridWidthsProps } from './types';

export const useGridWidths = ({
  headers,
  columns,
  column_widths_resized = [],
  width = 0,
}: GridWidthsProps): GridWidths => {
  const initial_calculations = useMemo(() => {
    return columns.reduce(
      (acc: ColumnCalculations, column) => {
        const column_width = column.props.width || 0;
        acc.widths.push(column_width);
        acc.min_width += column_width;
        acc.grow_sum += column.props.grow || 0;
        return acc;
      },
      { widths: [], min_width: 0, grow_sum: 0 }
    );
  }, [columns]);

  const {
    column_widths,
    total_columns_width,
    cumulative_fixed_left_widths,
    cumulative_fixed_right_widths,
  } = useMemo(() => {
    const { widths, min_width, grow_sum } = initial_calculations;
    const extra_space = Math.max(0, width - min_width);

    const column_widths = columns.map((column, index) => {
      if (column_widths_resized[index] !== undefined) {
        return column_widths_resized[index];
      }
      const grow = column.props.grow || 0;
      const extra_width = grow ? (grow / grow_sum) * extra_space : 0;
      return widths[index] + extra_width;
    });

    const total_columns_width = column_widths.reduce((sum, width) => sum + width, 0);

    const fixed_left_widths: number[] = new Array(column_widths.length).fill(0);
    const fixed_right_widths: number[] = new Array(column_widths.length).fill(0);
    let flat_index = 0;

    const processHeader = (header: ColumnElement, fixed_right = false) => {
      if (header.props.children) {
        React.Children.forEach(header.props.children, (child) => {
          if (React.isValidElement<GridColumnProps>(child)) {
            processHeader(child, fixed_right || header.props.fixed === 'right');
          }
        });
      } else {
        if (header.props.fixed === 'left') {
          fixed_left_widths[flat_index] = column_widths[flat_index];
        }
        if (header.props.fixed === 'right' || fixed_right) {
          fixed_right_widths[flat_index] = column_widths[flat_index];
        }
        flat_index++;
      }
    };

    headers.forEach((header) => processHeader(header));

    const cumulative_fixed_left_widths: number[] = fixed_left_widths.reduce(
      (acc, width) => [...acc, (acc[acc.length - 1] || 0) + width],
      [] as number[]
    );

    const cumulative_fixed_right_widths: number[] = fixed_right_widths
      .reverse()
      .reduce((acc, width) => [...acc, (acc[acc.length - 1] || 0) + width], [] as number[])
      .reverse();

    return {
      column_widths,
      total_columns_width,
      cumulative_fixed_left_widths,
      cumulative_fixed_right_widths,
    };
  }, [headers, columns, width, column_widths_resized, initial_calculations]);

  const cumulative_column_widths = useMemo(
    () =>
      column_widths.reduce(
        (acc, width) => [...acc, (acc[acc.length - 1] || 0) + width],
        [] as number[]
      ),
    [column_widths]
  );

  return {
    column_widths,
    cumulative_column_widths,
    cumulative_fixed_left_widths,
    cumulative_fixed_right_widths,
    total_columns_width,
  };
};
